import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { EMPTY } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { environment } from '@environments/environment';
import { isSessionExpiryHandled } from './session-expiry';

/**
 * HTTP Interceptor for adding authentication token to requests
 * Integrates with WiseAuth authentication system
 * Also adds withCredentials for cookie-based authentication
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const token = authService.getToken();

  // Check if this is an API request (needs credentials for cookies)
  const isApiRequest = req.url.includes(environment.apiUrl);

  // Skip auth header for public auth endpoints only
  const publicAuthEndpoints = [
    '/auth/login',
    '/auth/register',
    '/auth/reactivate',
    '/auth/check-user',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/verify-2fa',
    '/auth/refresh'
  ];

  const isPublicEndpoint = publicAuthEndpoints.some(endpoint => req.url.includes(endpoint));

  if (isSessionExpiryHandled() && !isPublicEndpoint) {
    return EMPTY;
  }

  // For public endpoints, still add withCredentials for cookie handling
  if (isPublicEndpoint) {
    const clonedRequest = req.clone({
      withCredentials: isApiRequest
    });
    return next(clonedRequest);
  }

  // Add authorization header if token exists, and withCredentials for cookies
  if (token) {
    const clonedRequest = req.clone({
      withCredentials: isApiRequest,
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
    return next(clonedRequest);
  }

  // For requests without token, still add withCredentials
  if (isApiRequest) {
    const clonedRequest = req.clone({
      withCredentials: true
    });
    return next(clonedRequest);
  }

  return next(req);
};

